<template>
  <div class="collection__container">

      <span class="collection__total-products sm" v-if="totalProducts > 1 && template == 'search'">
        Showing {{ totalProducts }} results
      </span>

      <!-- Filters -->
      <div class="collection__filters">

        <!-- Filter By -->
        <div class="filter__group group--filter-by" v-if="filterOptions.length">
          <div
            :class="['filter__toggle', { 'open': filterByOpen }]"
            @click="filterByOpen = !filterByOpen; sortByOpen = false">
            <span class="filter__title">Filter By</span>
            <IconChevron stroke="#EEEEEE" :direction="filterByOpen ? 'up' : 'down'"></IconChevron>
          </div>
          <TransitionExpand>
            <div class="filter__dropdown" v-show="filterByOpen">
              <div v-for="(filterGroup, index) in filterOptions" :key="index" class="filter__group--child">
                <div
                  :class="['filter__toggle', { 'open': filtersOpen.includes(index) }]"
                  @click="toggleFilterBtn(index); sortByOpen = false">
                  <span class="filter__title">{{ filterGroup.label }}</span>
                  <IconChevron stroke="#EEEEEE" :direction="filtersOpen.includes(index) ? 'up' : 'down'"></IconChevron>
                </div>
                <TransitionExpand linear_margin="1rem">
                  <div class="filter__dropdown--child" v-show="filtersOpen.includes(index)">
                    <span v-for="(option, index) in filterGroup.values"
                          :key="index"
                          @click="toggleFilter(option.key, stripKey(option.key), collectionID, $event)"
                          :class="['filter__option', showActive(option.key)]">
                      <span>{{ stripKey(option.key) }}</span>
                    </span>
                  </div>
                </TransitionExpand>
              </div>
            </div>
          </TransitionExpand>
        </div>

        <!-- Sort By -->
        <div class="filter__group group--sort-by">
          <div :class="['filter__toggle', { 'open': sortByOpen }]" @click="sortByOpen = !sortByOpen; filterByOpen = false">
            <span class="filter__title">Sort By: {{ sortByApplied || sort_order.replace('-', ' ') }}</span>
            <IconChevron stroke="#EEEEEE" :direction="sortByOpen ? 'up' : 'down'"></IconChevron>
          </div>
          <TransitionExpand>
            <div class="filter__dropdown" v-show="sortByOpen">
              <span
                @click="toggleFilter('sort by: new', 'new', collectionID)"
                :class="[ 'filter__option', showActive('new')]">
                New
              </span>
              <span
                @click="toggleFilter('sort by: lowest price', 'lowest price', collectionID)"
                :class="[ 'filter__option', showActive('lowest price')]">
                Lowest Price
              </span>
              <span
                @click="toggleFilter('sort by: highest price', 'highest price', collectionID)"
                :class="[ 'filter__option', showActive('highest price')]">
                Highest Price
              </span>
              <span
                @click="toggleFilter('sort by: best sellers', 'best sellers', collectionID)"
                :class="[ 'filter__option', showActive('best sellers')]">
                Best Sellers
              </span>
            </div>
          </TransitionExpand>
        </div>

      </div>

      <div v-if="filtersApplied.length" @click="clearFilters()" class="collection__clear-filters btn btn--close">
        <span><IconClose stroke="#62AF31" />Remove Filters</span>
      </div>

      <div v-if="!noProductsfound" class="collection__products">

        <!-- Products Grid -->
        <div class="collection__products-grid" :data-grid-size="grid_size">
          <div
            v-for="product in products"
            class="collection__products-item product-item"
            :key="product.id">
            <a class="product-item__link" :href="`/collections/${ handle }/products/${ product.handle }`">
              <!-- Labels -->
              <template v-for="label in labels">
                <div
                  v-if="!product.available && label.tag == 'sold-out'"
                  class="product-item__label"
                  v-html="label.svg"
                  :key="label">
                </div>
                <div
                  v-else-if="product.available && product.tags.includes(label.tag)"
                  class="product-item__label"
                  v-html="label.svg"
                  :key="label">
                </div>
              </template>
              <!-- Image -->
              <div class="product-item__image-wrapper lazy-container">
                <img data-sizes="auto" :data-src="product.images['1']" class="lazyload" :alt="product.title" />
              </div>
              <!-- Title -->
              <span class="product-item__title">
                {{ product.title }}
              </span>
              <!-- Price -->
              <p class="product-item__price">
                <span class="money">${{ product.variants[0].price }}</span>
                <span
                  class="product__sale-price"
                  v-if="product.variants[0].compare_at_price != null">
                  <s><span class="money">{{ product.variants[0].compare_at_price }}</span></s>
                </span>
              </p>
              <!-- Quick Shop -->
              <button class="product-item__quickshop btn btn--secondary" @click.prevent="quickshop(product.handle)">Quick Shop</button>
            </a>
            <!-- Wishlist -->
            <div
              ref="insert-wishlist"
              :id="product.id"
              class="product-item__wishlist">
            </div>
          </div>
        </div>

        <!-- Infinite scrolling pagination -->
        <infinite-loading v-if="!noProductsfound" :identifier="infiniteId" class="collection__products-infinite-loading" @infinite="infiniteHandler">
          <div slot="no-more"></div>
          <div slot="no-results"></div>
          <div slot="spinner">
            <div class="site-loading">
              <div class="half-circle-spinner">
                <div class="circle circle-1"></div>
                <div class="circle circle-2"></div>
              </div>
            </div>
          </div>
        </infinite-loading>

      </div>

      <!-- No products to load -->
      <div v-else class="no-results">
        <h3>Sorry, no products found</h3>
      </div>

  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import IconChevron from '../Icons/IconChevron'
import IconClose from '../Icons/IconClose'
import TransitionExpand from '../Transitions/TransitionExpand'

export default {
  props: [ 'id', 'handle', 'template', 'query', 'sort_order', 'grid_size', 'wishlist_enabled' ],

  components: { InfiniteLoading, IconChevron, IconClose, TransitionExpand },

  data () {
    return {
      products         : [],
      filterOptions    : [],            // The filters returned by boost commerce API
      filterByOpen     : false,         // The 'filter by' dropdown is open
      filtersOpen      : [],            // The filters which are open inside of the 'filter by' dropdown
      filterTagsApplied: [],            // The current filters entire tags applied (colour: blue, colour: red)
      filtersApplied   : [],            // The current filters tag values applied (blue, red)
      sortByOpen       : false,         // The 'sort by' dropdown is open
      sortByApplied    : '',            // The current sort by value applied
      labels           : [],
      noProductsfound  : false,
      page             : 1,
      infiniteId       : +new Date(),
      noProductsfound  : false,
      allProductsLoaded: false,
      totalProducts    : 0
    }
  },

  mounted () {
    this.loadCollection()
  },

  async created () {
    this.labels = await window.setLabels()
  },

  methods: {
    async loadCollection () {
      await this.getFilteredCollection({ collectionID: this.id, page: 1 })

      // Open all filters in 'filter by' by default
      const filter_keys = this.filterOptions.keys()
      for (const key of filter_keys) {
        this.filtersOpen.push(key)
      }

      this.products.length ? this.page = 1 : this.noProductsfound = true
    },

    quickshop (handle) {
      window.quickshop(handle)
    },

    toggleFilterBtn (idx) {
      if (this.filtersOpen.includes(idx)) {
        let index = this.filtersOpen.indexOf(idx)
        this.filtersOpen.splice(index, 1)
      } else {
        this.filtersOpen.push(idx)
      }
    },

    toggleFilter (tag, value, collectionID) {
      // Reset products
      this.products = []
      if (!this.filtersApplied.includes(tag)) {
        // Add filter
        if (tag.includes('sort by')) {
          this.sortByApplied = value
        } else {
          this.filterTagsApplied.push(tag)
          this.filtersApplied.push(tag)
        }
      } else {
        // Remove filter
        this.filterTagsApplied.splice(this.filterTagsApplied.indexOf(tag), 1)
        this.filtersApplied.splice(this.filtersApplied.indexOf(tag), 1)
      }
      this.getFilteredCollection({ collectionID: this.id, page: 1 })
      this.page = 1
    },

    clearFilters () {
      this.filterTagsApplied = []
      this.filtersApplied = []
      this.getFilteredCollection({ collectionID: this.id, page: 1 })
      this.page = 1
    },
      
    stripKey (tag) {
      if (/:(.+)/.exec(tag) && /:(.+)/.exec(tag).length) {
        return /:(.+)/.exec(tag)[1].trim()
      } else {
        return tag
      }
    },

    showActive (option) {
      if (this.filtersApplied.includes(option)) return 'active-filter'
      if (this.sortByApplied.includes(option)) return 'active-filter'
    },

    async getFilteredCollection ({ collectionID, page }) {
      // BCSF Endpoint
      let url = new URL('https://services.mybcapps.com/bc-sf-filter/')

      // Request options
      let params = {
        shop             : 'haakaa.myshopify.com',
        page             : page,
        limit            : 20,
        product_available: false,
        build_filter_tree: true,
        collection_scope : collectionID
      }

      // Collection vs Search
      if (this.query) {
        url.pathname = url.pathname + 'search'
        params.q = this.query
      } else {
        url.pathname = url.pathname + 'filter'
      }

      // Set sort order
      if (this.sortByApplied) {
        switch (this.sortByApplied) {
          case 'new':
            params.sort = 'published-descending'
            break
          case 'lowest price':
            params.sort = 'price-ascending'
            break
          case 'highest price':
            params.sort = 'price-descending'
            break
          case 'best sellers':
            params.sort = 'best-selling-descending'
        }
      } else {
        params.sort = this.sort_order
      }

      // Create query string from params object
      Object.keys(params).forEach(key => {
        url.searchParams.append(key, params[key])
      })

      // Insert filters
      if (this.filterTagsApplied.length) {
        this.filterTagsApplied.forEach(filter => {
          // Filter by tag
          let filterKey = filter.split(':')[0].trim().toLowerCase().replace(/\s+/g, '_').replace('sub_', '')
          url.searchParams.append(`pf_t_${filterKey}[]`, filter)
          // Uncomment if you do not wish to use AND conditions (exclusive filtering)
          // url.searchParams.append(`pf_t_${filterKey}_and_condition`, false)
        })
      }

      // Send request
      await fetch(url)
        .then(response => response.json())
        .then(data => {
          let products = data.products
          if (products.constructor === Object) {
            // 0 products to show
            this.products = []
          } else {
            // Show products
            this.products.push(...products)
            this.allProductsLoaded = products.length < 20
            this.totalProducts = data.total_product
            // Build filter tree
            this.filterOptions = data.filter.options.filter(group => group.values.length)
            // Activate paginator
            this.infiniteId += 1
          }
        })
        .catch(error => {
          console.log('error', error)
        })

      // Insert wishlist (app's mutation observer replaces the comment)
      if (this.wishlist_enabled) {
        this.$refs['insert-wishlist'].forEach(tile => {
          if (tile.childElementCount > 0) return
          let commentNode = document.createComment(` include 'wishlist-button-collection' with '${ tile.id }' `)
          tile.appendChild(commentNode)
        })
      }
    },

    infiniteHandler: function ($state) {
      setTimeout(async function () {
        this.page += 1
        await this.getFilteredCollection({ collectionID: this.id, page: this.page })
        if (this.allProductsLoaded) {
          $state.complete()
        } else {
          $state.loaded()
        }
      }.bind(this), 1000) // Let the initial loadCollection function run first
    }
  }
}
</script>
